import React, { useContext, useMemo, useRef, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { CRow } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { camelCase, extractName, encodeObj, getOriginalField, getSegmentField, idSegment, formatTableName } from '../../../../utils';
import { EventExplorerContext } from './EventExplorer';
import { useSelector } from 'react-redux';
import { DEFAULT_DATE_FORMAT, MERGE_REPORT_NAME, REPORT_NAMES, REPORT_TAB_TYPES, REPORT_TYPE, SUBSCRIBER_PATH, DEFAULT_OBJECT_SF } from '../../../../constants';
import { convertNameSegmentSort, formatNumber, formatNumberValue, handleChangeColV2, isNumber, isRateField } from '../../../../utils/UtilReport';
import { LoadingPanel } from './report-v2/LoadingTemplate';
import CustomHeaderCell from './report-v2/grid/CustomHeaderCell';
import CSwitch from '../../../migration/CSwitch';
import { CSmartPagination } from '@coreui/react-pro';
import { TableCellValueUndefined, UserSourceDetails } from './report-v2/FieldCustom';
import TooltipContentTemplate from './report-v2/TooltipContentTemplate';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

const CustomCellCompare = ({ cellProps, listSegmentReportApply, arrShowShortIds, newDimensions, showShortId, isLoading, reportName, showTooltip, setRectCell }) => {
     const { dataItem, field, style, className, id } = cellProps;
     const value = dataItem[field];
     const originalField = getOriginalField(field, listSegmentReportApply).replace('Compare', '');
     const noFormat = ['itemId', 'transactionId', 'phone', 'utmContent', 'utm_content', 'utmTerm', 'utmCampaign', 'utmMedium', 'utmSource', 'postalCodeCustom'];
     const refCell = useRef(null);
     if (arrShowShortIds.includes(originalField) && newDimensions.find((dim) => dim.key === originalField)) {
          return (
               <td
                    ref={refCell}
                    onClick={
                         value !== 'undefine'
                              ? (event) => {
                                     showTooltip(refCell);
                                     setRectCell(event.currentTarget.getBoundingClientRect());
                                }
                              : null
                    }
                    {...{ className }}
                    style={{ ...style, textAlign: 'center', cursor: value !== 'undefine' ? 'pointer' : 'default' }}
                    tabIndex={0}
                    data-keyboardnavid={id}
                    title={' '}
                    data-title={value}
               >
                    {showShortId.includes(originalField) ? (
                         <i style={{ pointerEvents: 'none' }}>
                              <CIcon icon='iconFingerprint' style={{ color: '#63c2de' }} />
                         </i>
                    ) : (
                         <span style={{ pointerEvents: 'none' }}>{value}</span>
                    )}
               </td>
          );
     }

     if (value === undefined) {
          const hasOwnProperty = dataItem.hasOwnProperty(field);
          return TableCellValueUndefined(isLoading, hasOwnProperty);
     } else if (
          ['userSourcePath', 'userSourcePathND'].includes(field) &&
          [REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION, REPORT_NAMES.SALES_CONVERSION_BY_PROPERTY, REPORT_NAMES.BY_CONVERSION, REPORT_NAMES.BY_PROPERTY].includes(
               reportName,
          )
     ) {
          return UserSourceDetails(value, cellProps, {}, true);
     } else {
          return typeof value === 'boolean' || noFormat.includes(field) ? (
               <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                    <span>{String(value)}</span>
               </td>
          ) : (
               <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                    {`${formatNumber(value)}`}
               </td>
          );
     }
};

CustomCellCompare.propTypes = {
     cellProps: PropTypes.shape({
          dataItem: PropTypes.object.isRequired,
          field: PropTypes.string.isRequired,
          style: PropTypes.object,
          className: PropTypes.string,
          id: PropTypes.string,
     }).isRequired,
     listSegmentReportApply: PropTypes.array,
     arrShowShortIds: PropTypes.array,
     newDimensions: PropTypes.array,
     showShortId: PropTypes.array,
     isLoading: PropTypes.bool,
     reportName: PropTypes.string,
     showTooltip: PropTypes.func.isRequired,
     setRectCell: PropTypes.func.isRequired,
};

const ReportTableCompare = (props) => {
     const {
          reportName,
          filterDimensions,
          setFilterDimensions,
          filterProperties,
          setFilterProperties,
          isLoading,
          reportType,
          reportData,
          reportCompare,
          itemsPerPage,
          setItemsPerPage,
          externalObjectMetric,
          filterMetric,
          setFilterMetric,
          setOffSet,
          handleFindView,
          listSegmentReportApply,
          tempScopedReport,
          newDimensions: dimensionsAll,
          staticLinkKey,
          eCoItemCategories,
          utmArray,
          recordProfile,
          salesForceObjects,
          customExternalObjects,
     } = useContext(EventExplorerContext);

     let dimensions = [...dimensionsAll];
     if (reportName === REPORT_NAMES.CONVERSION_SOURCE_ATTRIBUTION_REPORT) {
          dimensions = [...dimensionsAll, ...utmArray];
     }
     if (reportName === REPORT_NAMES.ECOMMERCE_ITEMS) {
          dimensions = [...dimensionsAll, ...eCoItemCategories];
     }
     const newDimensions = handleChangeColV2(dimensions, filterDimensions);

     const widthColumnReports = useSelector((state) => state.subscriber.widthColumnReports);
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const dateRangeReports = useSelector((state) => state.subscriber.dateRangeReports);
     const customExternalObjectReport = useSelector((state) => state.subscriber.customExternalObjectReport);
     const isExploreReport = !reportType || reportType === REPORT_TYPE.SALE_FORCE || reportName === REPORT_NAMES.ECOMMERCE_PURCHASE_TRANSACTION;

     const {
          getWidthByKey,
          tableGrid,
          handleColumnResize,
          lock,
          activePage,
          setActivePage,
          arrShowShortIds,
          showLock,
          showShortId,
          sort,
          disableSort,
          setLock,
          setShowShortId,
     } = props;
     const [pagination, setPagination] = useState(true);
     const [openTooltip, setOpenTooltip] = useState(false);
     const [targetElement, setTargetElement] = useState(null);
     const [showCopyTooltip, setShowCopyTooltip] = useState(true);
     const [rectCell, setRectCell] = useState({});
     const [positionTooltip, setPositionTooltip] = useState('right');
     const totalPages = Math.ceil(reportData.overallRowCount / itemsPerPage) || 1;

     const tableData = useMemo(() => [...reportCompare.rows], [reportCompare.rows]); // eslint-disable-line

     const handleChangePagination = () => {
          setPagination(!pagination);
          setActivePage(1);
          setOffSet(0);
     };

     const handleLinkToOtherReport = (fieldKey, linkedData, field) => {
          let path = '';
          let linkedReportData;
          let filters = [];
          let startDate;
          let tempScoped = { tempScoped:'' };

          if(field.includes('Compare')){
               startDate = dateRangeReports.compare?.startDate.format(DEFAULT_DATE_FORMAT)
          } else {
               startDate = dateRangeReports.selection?.startDate.format(DEFAULT_DATE_FORMAT)
          }

          if(dateRangeReports.selection?.startDate.format(DEFAULT_DATE_FORMAT) === startDate){
               tempScoped.tempScoped = tempScopedReport.tempScoped
          }

          if(dateRangeReports.compare?.startDate.format(DEFAULT_DATE_FORMAT) === startDate){
               tempScoped.tempScoped = tempScopedReport.tempScopedCompare
          }

          const segmentClick = field && listSegmentReportApply?.find((item) => field.includes(camelCase(item.id)));
          let isNewCount = false;
          let isBlocked = false;
          let isCustomExternalPath = false;

          const externalMetricField = externalObjectMetric.find(item => item.key === fieldKey);
          const isConversion = [REPORT_NAMES.BY_CONVERSION, REPORT_NAMES.BY_PROPERTY].includes(reportName)
          const isSalesConversion = reportName === REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION;
          switch (fieldKey) {
               case 'sessionCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_SESSIONS;
                    newDimensions.forEach((item) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,
                         });
                    });

                    break;

               case 'userCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_USERS;
                    newDimensions.forEach((item) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,
                         });
                    });

                    break;

               case 'peopleCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_PEOPLE;
                    newDimensions.forEach((item) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,                         
                         });
                    });

                    break;

               case 'revealedCompany':
               case 'companyCount':
                    path = SUBSCRIBER_PATH.INSIGHTS_REVEALED_COMPANIES;
                    newDimensions.forEach((item) => {
                         filters.push({
                              type: item.key,
                              value: isConversion ? extractName(linkedData[item.key]) : `${linkedData[item.key]}`,
                         });
                    });

                    break;
               case 'newSessionCount': 
                    path = SUBSCRIBER_PATH.INSIGHTS_SESSIONS;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case 'newUserCount': 
                    path = SUBSCRIBER_PATH.INSIGHTS_USERS;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case 'newPeopleCount': 
                    path = SUBSCRIBER_PATH.INSIGHTS_PEOPLE;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case 'newCompanyCount': 
                    path = SUBSCRIBER_PATH.INSIGHTS_REVEALED_COMPANIES;
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                         });
                    });
                    isNewCount = true;

                    break;
               case `${externalMetricField?.key}`: 
                    switch (fieldKey) {
                         case 'activeLeadCount':
                         case 'newLeadCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_LEADS;
                              break;
                         case 'activeContactCount':
                         case 'newContactCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CONTACTS;
                              break;
                         case 'activeAccountCount':
                         case 'newAccountCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_ACCOUNTS;
                              break;
                         case 'activeOpportunityCount':
                         case 'newOpportunityCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_OPPORTUNITIES;
                              break;
                         case 'activeCampaignCount':
                         case 'newCampaignCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CAMPAIGNS;
                              break;
                         case 'activeEventCount':
                         case 'newEventCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CRM_EVENTS;
                              break;
                         case 'activeQuoteCount':
                         case 'newQuoteCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_QUOTES;
                              break;
                         case 'activeOrderCount':
                         case 'newOrderCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_ORDERS;
                              break;
                         case 'activeContractCount':
                         case 'newContractCount':
                              path = SUBSCRIBER_PATH.INSIGHTS_CONTRACTS;
                              break;
                         case `active${externalMetricField?.object}Count`:
                         case `new${externalMetricField?.object}Count`:
                              path = `/:secondId/explore-data/crm-${externalMetricField?.object}`;
                              break;
                         default:
                              break;
                    }
                    newDimensions.forEach((item, index) => {
                         filters.push({
                              type: item.key,
                              value: `${linkedData[item.key]}`,
                              object: externalMetricField?.object,
                         });
                    });
                    isNewCount = fieldKey.includes('new');
                    break;
               case 'conversionCount':
                    if (isSalesConversion) {
                         const primaryObj = extractName(linkedData.primaryObjectClick);
                         if (primaryObj) {
                              const isCustomExternal = customExternalObjectReport.some(
                                   (item) => formatTableName({ name: item?.object, prefix: '', isObject: true }) === primaryObj
                              );

                              if (isCustomExternal) {
                                   path = `/:secondId/explore-data/custom-external-${formatTableName({ name: primaryObj, prefix: '', isObject: true }).toLowerCase()}`;
                                   isCustomExternalPath = true;
                              } else if (DEFAULT_OBJECT_SF[primaryObj]) {
                                   path = `/:secondId/explore-data/crm-${primaryObj.toLowerCase()}`;
                              } else {
                                   path = `/:secondId/explore-data/crm-${primaryObj.toLowerCase()}`;
                              }
                              if(![...salesForceObjects, ...customExternalObjects].some(obj => obj?.object === primaryObj)){
                                   isBlocked = true;
                              }
                         }

                         newDimensions.forEach((item) => {
                              filters.push({
                                   type: item.key,
                                   value: extractName(linkedData[item.key]),
                                   object: primaryObj,
                              });
                         });
                    }
                    break;
               default:
          }

          if(isBlocked){
               toast.error('The object is disabled, so the report ca\'t be shown. Please enable it again!')
               return;
          }

          linkedReportData = {
               filters,
               segmentId: segmentClick?.id,
               tempScopedReport:tempScoped,
               isCompare: field.includes('Compare'),
               isNewCount,
               fieldName: fieldKey,
          };

          path = path.replace(':secondId', activeAccount.secondId) + '?staticKey=' + staticLinkKey + '&dynamicKey=' + encodeObj(linkedReportData);
          if (isCustomExternalPath) {
               path += '&show-custom-external-report=1';
          }
          window.open(path, '_blank');
     };
     const showTooltip = (element, isHiddenCopy) => {
          isHiddenCopy ? setShowCopyTooltip(false) : setShowCopyTooltip(true);
          setOpenTooltip(true);
          setTargetElement(element.current);
     };

     const handleEnterToLink = (e) => {
          if (e.which === 13) e.currentTarget.click();
     };

     const getFieldData = (data, field, fieldName) => {
          if (field.includes('Compare')) {
               fieldName = fieldName + 'Compare';
          }
          if (typeof data[fieldName] !== 'undefined') {
               return data[fieldName];
          }
          return data[`${field.split('_')[0]}_${fieldName}`];
     };
     
     const renderSessionCount = (cellProps, handleEnterToLink, handleLinkToOtherReport, reportName, originalField) => {
          const { style, className, id, field, dataItem } = cellProps;
          const sessionCount = getFieldData(dataItem, field, originalField);
          if (sessionCount > 0 && ![REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION,REPORT_NAMES.SALES_CONVERSION_BY_PROPERTY].includes(reportName)) {
               return (
                    <td
                         style={style}
                         className={`clickable ${className}`}
                         tabIndex={0}
                         data-keyboardnavid={id}
                         onKeyPress={(e) => handleEnterToLink(e)}
                         onClick={() => {
                              return handleLinkToOtherReport(originalField, { ...dataItem }, field);
                         }}
                    >{`${formatNumberValue(sessionCount)}`}</td>
               );
          }

          return (
               <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                    {sessionCount ? formatNumberValue(sessionCount) : 0}
               </td>
          );
     };

     const renderUserCount = (cellProps, handleEnterToLink, handleLinkToOtherReport, reportName) => {
          const { style, className, id, field, dataItem } = cellProps;
          const userCount = getFieldData(dataItem, field, 'userCount');
          if (userCount > 0 && ![REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION,REPORT_NAMES.SALES_CONVERSION_BY_PROPERTY].includes(reportName)) {
               return (
                    <td
                         style={style}
                         className={`clickable ${className}`}
                         tabIndex={0}
                         data-keyboardnavid={id}
                         onKeyPress={(e) => handleEnterToLink(e)}
                         onClick={() => {
                              return handleLinkToOtherReport('userCount', { ...dataItem }, field);
                         }}
                    >{`${formatNumberValue(userCount)}`}</td>
               );
          }

          return (
               <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                    {userCount ? formatNumberValue(userCount) : 0}
               </td>
          );
     };

     const renderPeopleCount = (cellProps, handleEnterToLink, handleLinkToOtherReport) => {
          const { style, className, id, field, dataItem } = cellProps;
          const peopleCount = getFieldData(dataItem, field, 'peopleCount');
          if (peopleCount > 0) {
               return (
                    <td
                         style={style}
                         className={`clickable ${className}`}
                         tabIndex={0}
                         data-keyboardnavid={id}
                         onKeyPress={(e) => handleEnterToLink(e)}
                         onClick={() => {
                              return handleLinkToOtherReport('peopleCount', { ...dataItem }, field);
                         }}
                    >{`${formatNumberValue(peopleCount)}`}</td>
               );
          }

          return (
               <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                    {peopleCount ? formatNumberValue(peopleCount) : 0}
               </td>
          );
     };

     const renderRevealedCompany = (cellProps, handleEnterToLink, handleLinkToOtherReport, originalField) => {
          const { style, className, id, field, dataItem } = cellProps;
          const revealedCompany = getFieldData(dataItem, field, originalField);
          if (revealedCompany > 0) {
               return (
                    <td
                         style={style}
                         className={`clickable ${className}`}
                         tabIndex={0}
                         data-keyboardnavid={id}
                         onKeyPress={(e) => handleEnterToLink(e)}
                         onClick={() => {
                              return handleLinkToOtherReport(originalField, { ...dataItem }, field);
                         }}
                    >{`${formatNumberValue(revealedCompany)}`}</td>
               );
          }

          return (
               <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                    {revealedCompany ? formatNumberValue(revealedCompany) : 0}
               </td>
          );
     };

     const renderLinkMetricCount = (cellProps, handleEnterToLink, handleLinkToOtherReport, reportName, originalField) => {
          const { style, className, id, field, dataItem } = cellProps;
          const valueCount = getFieldData(dataItem, field, originalField);
          if (valueCount > 0 && MERGE_REPORT_NAME.includes(reportName)) {
               return (
                    <td
                         style={style}
                         className={`clickable ${className}`}
                         tabIndex={0}
                         data-keyboardnavid={id}
                         onKeyPress={(e) => handleEnterToLink(e)}
                         onClick={() => {
                              return handleLinkToOtherReport(originalField, { ...dataItem }, field);
                         }}
                    >{`${formatNumberValue(valueCount)}`}</td>
               );
          }

          return <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>{`${formatNumberValue(valueCount)}`}</td>;
     };

     const renderSalesConversionCount = (cellProps, handleEnterToLink, handleLinkToOtherReport, reportName, originalField) => {
          const { style, className, id, field, dataItem } = cellProps;
          const value = getFieldData(dataItem, field, originalField);
          if (value > 0 && reportName === REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION) {
               return (
                    <td
                         style={style}
                         className={`clickable ${className}`}
                         tabIndex={0}
                         data-keyboardnavid={id}
                         onKeyPress={(e) => handleEnterToLink(e)}
                         onClick={() => {
                              return handleLinkToOtherReport(originalField, { ...dataItem }, field);
                         }}
                    >{`${formatNumberValue(value)}`}</td>
               );
          }
          return (
               <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                    {value ? formatNumberValue(value) : 0}
               </td>
          );
     };

     const customCellCompareValue = ({ cellProps, color }) => {
          const originalField = getOriginalField(cellProps.field, listSegmentReportApply).replace('Compare', '');

          const value = cellProps.dataItem[cellProps.field];
          let { style, className, id } = cellProps;

          if (color) {
               className = className + ` compare-${color} `;
          }

          if (value === undefined) {
               const hasOwnProperty = cellProps.dataItem.hasOwnProperty(cellProps.field);
               return TableCellValueUndefined(isLoading, hasOwnProperty);
          }

          if ( isRateField({ value: cellProps.field }) ) {
               return <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>{`${formatNumberValue(value)}${value !== '' ? '%': ''}`}</td>;
          }

          if (originalField.includes('ObjectCreated')) {
               return (
                    <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                         <span>{value ? formatNumberValue(Number(value)) : ''}</span>
                    </td>
               );
          }

          if (externalObjectMetric.some((item) => item.key === originalField)) {
               return renderLinkMetricCount(cellProps, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);
          }

          switch (originalField) {
               case 'avgSessionDuration':
               case 'sessionDuration':
               case 'lifeTimeAvgSessionDuration':
                    return (
                         <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                              {value}
                         </td>
                    );
               case 'avgVisibleTimeOnPage':
                    return (
                         <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                              {value || 0}
                         </td>
                    );
               case 'avgVisibleTimePerPage':
                    return (
                         <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                              {value}
                         </td>
                    );

               case 'sessionCount':
                    return renderSessionCount({...cellProps, className}, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);

               case 'newSessionCount':
               case 'newUserCount':
               case 'newPeopleCount':
               case 'newCompanyCount':
                    return renderLinkMetricCount({...cellProps, className}, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);

               case 'userCount':
                    return renderUserCount({...cellProps, className}, handleEnterToLink, handleLinkToOtherReport, reportName);

               case 'peopleCount':
                    return renderPeopleCount({...cellProps, className}, handleEnterToLink, handleLinkToOtherReport);

               case 'revealedCompany':
               case 'companyCount':
                    return renderRevealedCompany({...cellProps, className}, handleEnterToLink, handleLinkToOtherReport, originalField);

               case 'conversionCount':
                    return renderSalesConversionCount({...cellProps, className}, handleEnterToLink, handleLinkToOtherReport, reportName, originalField);

               default:
                    return typeof value === 'boolean' ? (
                         <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                              <span>{String(value)}</span>
                         </td>
                    ) : (
                         <td {...{ style, className }} tabIndex={0} data-keyboardnavid={id}>
                              {`${formatNumberValue(value)}`}
                         </td>
                    );
          }
     };

     const getTotalCellCompare = (field) => {
          const totalValue = reportCompare.totalsRow.totalNor;
          const totalCompare = reportCompare.totalsRow.totalCompare;
          let total
          if ((totalValue?.length > 0) || (totalCompare?.length > 0)) {
               const fieldDuration = field.includes('Duration') || field.includes('VisibleTime');
               const totalCell = totalValue ? totalValue[0].metricCompoundValues.find((item) => item.key === field) : { value: !fieldDuration ? 0 : `0m 0s` };
               const totalCellCompare = totalCompare ? totalCompare[0].metricCompoundValues.find((item) => item.key === field.replace('Compare', '')) : { value: !fieldDuration ? 0 : `0m 0s` };
               total = field.includes('Compare') ? totalCellCompare : totalCell
               if (total) {
                    if (!isNumber(total.value)) {
                         return `${total.value}`;
                    } else if ( isRateField({ value: field }) ) {
                         return `${formatNumberValue(total.value)}%`;
                    } else {
                         return `${formatNumberValue(total.value)}`;
                    }
               }
          }
     };

     const onChangeItemsPerPage = (e) => {
          setActivePage(1);
          setOffSet(0);
          setItemsPerPage(+e.target.value);
     };

     const onPaginationChange = (i) => {
          setOffSet((i - 1) * itemsPerPage)
          setActivePage(i);
     };

     const mergeMetrics = useMemo(() => [...filterMetric], [filterMetric])
     const uniqByMetrics = uniqBy(mergeMetrics, 'label');

     const handleColumnReorder = ({ columns, columnId }) => {
          columns.forEach((column) => {
              column.children.some((child) => {
                  if (child.id === columnId) {
                      const sortedChildren = [...column.children].sort((a, b) => a.orderIndex - b.orderIndex);
                      const filterChildren = sortedChildren.filter(_child => _child.title === child.title);

                      const beforeChildren = sortedChildren.filter(_child => _child.orderIndex < child.orderIndex && _child.title !== child.title);
                      const afterChildren = sortedChildren.filter(_child => _child.orderIndex > child.orderIndex && _child.title !== child.title);

                      const reorderedColumnsCustom = [...beforeChildren, ...filterChildren, ...afterChildren];

                      const reorderedColumns = reorderedColumnsCustom.map((child) => ({
                          label: child.title,
                          value: convertNameSegmentSort({sort: {fieldName: child.field}, listSegmentReportApply}),
                      }));

                      switch (column.title) {
                          case 'Dimensions':
                              setFilterDimensions(reorderedColumns);
                              handleFindView(REPORT_TAB_TYPES.DIMENSIONS, reorderedColumns, false);
                              break;
                          case 'Properties':
                              setFilterProperties(reorderedColumns);
                              handleFindView(REPORT_TAB_TYPES.PROPERTIES, reorderedColumns, false);
                              break;
                          case 'Metrics':
                              setFilterMetric(uniqBy(reorderedColumns, 'label'));
                              handleFindView(REPORT_TAB_TYPES.METRICS, reorderedColumns, false);
                              break;
                          default:
                              break;
                      }

                      return true;
                  }

                  return false;
              });
          });
     };
     const changePositionTooltip = (props) => {
          let position = 'right';
          let left = props.mouseLeft + 10;
          let top = props.mouseTop - props.element.clientHeight / 2;

          if (rectCell && newDimensions[0] && arrShowShortIds.includes(newDimensions[0].key)) {
               left = rectCell.left + rectCell.width / 2 + 20;
               top = rectCell.top - props.element.clientHeight / 2 + 15;
          }

          if (props.mouseLeft + 360 > document.body.clientWidth) {
               left = props.mouseLeft - (props.element.clientWidth + 10);
               position = 'left';
          }

          setPositionTooltip(position);
          return { left, top };
     };
     return (
          <>
               {isLoading && pagination && <LoadingPanel />}
               <Tooltip
                    className={`${recordProfile ? 'tooltip-profile' : ''}`}
                    onPosition={changePositionTooltip}
                    tooltipStyle={{ background: '#050505' }}
                    content={(props) => <TooltipContentTemplate props={props} showCopyTooltip={showCopyTooltip} />}
                    openDelay={0}
                    open={openTooltip}
                    targetElement={targetElement}
                    position={positionTooltip}
               >
                    <Grid
                         ref={tableGrid}
                         style={{ width: 'fit-content', maxWidth: '100%', maxHeight: pagination ? '1500px' : '800px' }}
                         data={tableData.length === 0 && isLoading ? Array(5).fill({}) : tableData}
                         resizable={true}
                         reorderable={!isLoading}
                         navigatable={true}
                         scrollable="scrollable"
                         onColumnReorder={handleColumnReorder}
                         onColumnResize={handleColumnResize}
                         className={`compare-report-table ${filterMetric?.length > 0 ? 'metric-hidden' : ''}`}
                         onScroll={(event) => {
                              if (openTooltip) {
                                   setOpenTooltip(false);
                              }
                         }}
                    >
                         <GridNoRecords>No results found</GridNoRecords>

                         {filterDimensions.length > 0 &&
                              <GridColumn orderIndex={0} title="Dimensions" resizable={false}>
                                   {filterDimensions.map((item, index) => {
                                        return (
                                             <GridColumn
                                                  key={index}
                                                  title={item.label}
                                                  field={item.value}
                                                  orderIndex={index}
                                                  width={getWidthByKey({
                                                       data: widthColumnReports,
                                                       reportType,
                                                       reportName,
                                                       label: item.label,
                                                       key: item.value,
                                                       tableData,
                                                       index
                                                  })}
                                                  headerCell={(props) => (
                                                       <CustomHeaderCell
                                                            props={props}
                                                            dimensions={newDimensions}
                                                            arrShowShortIds={arrShowShortIds}
                                                            showLock={showLock}
                                                            lock={lock}
                                                            showShortId={showShortId}
                                                            sort={sort}
                                                            disableSort={disableSort}
                                                            setLock={setLock}
                                                            setShowShortId={setShowShortId}
                                                       />
                                                  )}
                                                  cell={(cellProps) => CustomCellCompare({
                                                       cellProps,
                                                       listSegmentReportApply,
                                                       arrShowShortIds,
                                                       newDimensions,
                                                       showShortId,
                                                       isLoading,
                                                       reportName,
                                                       showTooltip,
                                                       setRectCell,
                                                  })}
                                                  locked={!!lock[item.value]}
                                                  footerCell={(props) => {
                                                       if (index + 1 === Number(filterDimensions.length) + Number(filterProperties.length)) {
                                                            return (
                                                                 <td
                                                                      className={props.className}
                                                                      style={{ ...props.style, textAlign: 'right', borderLeft: 'none' }}
                                                                 >
                                                                      Totals
                                                                 </td>
                                                            );
                                                       } else {
                                                            return <td className={props.className} style={{ ...props.style, borderLeft: 'none' }}></td>;
                                                       }
                                                  }}
                                             />
                                        );
                                   })}
                              </GridColumn>
                         }

                         {filterProperties.length > 0 &&
                              <GridColumn orderIndex={1} title="Properties" resizable={false}>
                                   {filterProperties.map((item, index) => {
                                        return (
                                             <GridColumn
                                                  key={index}
                                                  title={item.label}
                                                  field={item.value}
                                                  orderIndex={index}
                                                  width={getWidthByKey({
                                                       data: widthColumnReports,
                                                       reportType,
                                                       reportName,
                                                       label: item.label
                                                  })}
                                                  headerCell={(props) => (
                                                       <CustomHeaderCell
                                                            props={props}
                                                            dimensions={newDimensions}
                                                            arrShowShortIds={arrShowShortIds}
                                                            showLock={showLock}
                                                            lock={lock}
                                                            showShortId={showShortId}
                                                            sort={sort}
                                                            disableSort={disableSort}
                                                            setLock={setLock}
                                                            setShowShortId={setShowShortId}
                                                       />
                                                  )}
                                                  cell={(props) => customCellCompareValue({ cellProps: props })}
                                                  locked={!!lock[item.value]}
                                                  footerCell={(props) => {
                                                       if (index + 1 === Number(filterProperties.length)) {
                                                            return (
                                                                 <td
                                                                      className={props.className}
                                                                      style={{ ...props.style, textAlign: 'right', borderLeft: 'none' }}
                                                                 >
                                                                      Totals
                                                                 </td>
                                                            );
                                                       } else {
                                                            return <td className={props.className} style={{ ...props.style, borderLeft: 'none' }}></td>;
                                                       }
                                                  }}
                                             />
                                        );
                                   })}
                              </GridColumn>
                         }

                         {mergeMetrics.length > 0 &&
                              (listSegmentReportApply.length > 0 ? (
                                        <GridColumn orderIndex={2} title="Metrics" resizable={false}>
                                             {  uniqByMetrics.flatMap((metric, metricIndex) => (
                                                       listSegmentReportApply.map((seg, segIndex) => {
                                                            const valueSegment = idSegment(metric.value.split('_')[0]) ? metric.value.replace(metric.value.split('_')[0]+'_', '') : metric.value;
                                                            const item = {
                                                                 label: metric.label,
                                                                 value: `${getSegmentField(seg.id, valueSegment)}`
                                                       }
                                                       const orderIndex = metricIndex * listSegmentReportApply.length + (segIndex + 1);
                                                       return (
                                                            <GridColumn
                                                                 key={`${item.value}_${orderIndex}`}
                                                                 title={item.label}
                                                                 field={item.value}
                                                                 orderIndex={orderIndex}
                                                                 width={(getWidthByKey({
                                                                      data: widthColumnReports,
                                                                      reportType,
                                                                      reportName,
                                                                      label: item.label,
                                                                      key: item.value,
                                                                      tableData,
                                                                      index: orderIndex,
                                                                      isMetric: true,
                                                                      newLabel: true
                                                                 }) * 2) - 1}
                                                                 headerCell={(props) => (
                                                                      <div className="custom-header-segment d-flex align-items-center justify-content-between">
                                                                           <CustomHeaderCell
                                                                                props={props}
                                                                                dimensions={newDimensions}
                                                                                arrShowShortIds={arrShowShortIds}
                                                                                showLock={showLock}
                                                                                lock={lock}
                                                                                showShortId={showShortId}
                                                                                sort={sort}
                                                                                disableSort={disableSort}
                                                                                setLock={setLock}
                                                                                setShowShortId={setShowShortId}
                                                                           />
                                                                           <div className="rule-segment-item-icon d-flex">
                                                                                <CIcon
                                                                                     icon="iconChartPie"
                                                                                     style={{
                                                                                          cursor: 'pointer',
                                                                                          width: '14px',
                                                                                          height: '14px',
                                                                                          marginRight: '5px',
                                                                                     }}
                                                                                />
                                                                                <span className="rule-segment-item-level">S{segIndex + 1}</span>
                                                                           </div>
                                                                      </div>
                                                                 )}
                                                                 locked={!!lock[item.value]}
                                                            >
                                                                 {['FFB300', 'D1D5DB'].map((color, key) => (
                                                                      <GridColumn
                                                                           key={key}
                                                                           title={item.label}
                                                                           field={`${key === 0 ? item.value : `${item.value}Compare`}`}
                                                                           orderIndex={key}
                                                                           width={getWidthByKey({
                                                                                data: widthColumnReports,
                                                                                reportType,
                                                                                reportName,
                                                                                label: item.label,
                                                                                key: item.value,
                                                                                tableData,
                                                                                index: orderIndex,
                                                                                isMetric: true,
                                                                                newLabel: true
                                                                           })}
                                                                           cell={(props) => customCellCompareValue({ cellProps: props, color })}
                                                                           footerCell={(props) => {
                                                                                const totalCell = getTotalCellCompare(props.field);
                                                                                const compareClass = `compare-${color}`;
                                                                                return (
                                                                                     <td
                                                                                          className={props.className + ` ${totalCell !== undefined ? compareClass : ''} `}
                                                                                          style={props.style}
                                                                                          width={props.width}
                                                                                     >
                                                                                          {totalCell || totalCell === 0 ? totalCell : ''}
                                                                                     </td>
                                                                                );
                                                                           }}
                                                                           locked={!!lock[item.value]}
                                                                      />
                                                                 ))}
                                                            </GridColumn>
                                                       );

                                                       })
                                                  ))
                                             }
                                        </GridColumn>
                                   ) : (
                                        <GridColumn orderIndex={2} title="Metrics" resizable={false}>
                                             {mergeMetrics.map((item, index) => {
                                                  return (
                                                       <GridColumn
                                                            key={index}
                                                            title={item.label}
                                                            field={item.value}
                                                            orderIndex={index}
                                                            locked={!!lock[item.value]}
                                                            headerCell={(props) => (
                                                                 <CustomHeaderCell
                                                                      props={props}
                                                                      dimensions={newDimensions}
                                                                      arrShowShortIds={arrShowShortIds}
                                                                      showLock={showLock}
                                                                      lock={lock}
                                                                      showShortId={showShortId}
                                                                      sort={sort}
                                                                      disableSort={disableSort}
                                                                      setLock={setLock}
                                                                      setShowShortId={setShowShortId}
                                                                 />
                                                            )}
                                                       >
                                                            {['FFB300', 'D1D5DB'].map((color, key) => (
                                                                 <GridColumn
                                                                      key={key}
                                                                      title={item.label}
                                                                      field={`${key === 0 ? item.value : `${item.value}Compare`}`}
                                                                      orderIndex={key}
                                                                      width={getWidthByKey({
                                                                           data: widthColumnReports,
                                                                           reportType,
                                                                           reportName,
                                                                           label: item.label,
                                                                           key: item.value,
                                                                           tableData,
                                                                           index,
                                                                           isMetric: true,
                                                                           newLabel: true
                                                                      })}
                                                                      cell={(props) => customCellCompareValue({ cellProps: props, color })}
                                                                      footerCell={(props) => {
                                                                           const totalCell = getTotalCellCompare(props.field);
                                                                           const compareClass = `compare-${color}`;
                                                                           return (
                                                                                <td className={props.className + ` ${totalCell !== undefined ? compareClass : ''} `}
                                                                                     style={props.style}
                                                                                     width={props.width}
                                                                                >
                                                                                     {totalCell || totalCell === 0 ? totalCell : ''}
                                                                                </td>
                                                                           );
                                                                      }}
                                                                      locked={!!lock[item.value]}
                                                                 />
                                                            ))}
                                                       </GridColumn>
                                                  );
                                             })}
                                   </GridColumn>
                                   )
                              )
                         }

                    </Grid>
               </Tooltip>
               <CRow className={`report-table-bottom ${!pagination ? 'justify-content-end' : ''}`}>
                    {pagination && (
                         <div className="d-flex px-0">
                              <CSmartPagination
                                   activePage={activePage}
                                   pages={totalPages}
                                   onActivePageChange={onPaginationChange}
                                   className="report-table-page"
                              />
                              <div className="form-inline justify-content-sm-end ml-4">
                                   <select className="form-control" onChange={onChangeItemsPerPage} defaultValue={itemsPerPage}>
                                        <option value="5">5</option>
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                   </select>
                                   <label className="ml-2">Items per page {reportData.overallRowCount ? `| ${formatNumber(reportData.overallRowCount)} total ${reportData.overallRowCount > 1 ? `${isExploreReport ? 'records' : 'rows'}` : `${isExploreReport ? 'record' : 'row'}`}` : ''}</label>
                              </div>
                         </div>
                    )}
                    {!pagination && (
                         <div className="toggle-bottom">
                              <CSwitch
                                   className="mx-1"
                                   variant="3d"
                                   color="primary"
                                   checked={pagination ? 'checked' : ''}
                                   onChange={(e) => handleChangePagination()}
                                   disabled={true}
                              />
                              <span>Paging</span>
                         </div>
                    )}
               </CRow>
          </>
     );
};

ReportTableCompare.propTypes = {
     getWidthByKey: PropTypes.func,
     tableGrid: PropTypes.any,
     handleColumnResize: PropTypes.func,
     lock: PropTypes.any,
     activePage: PropTypes.any,
     setActivePage: PropTypes.func,
     arrShowShortIds: PropTypes.any,
     showLock: PropTypes.bool,
     showShortId: PropTypes.bool,
     sort: PropTypes.object,
     disableSort: PropTypes.bool,
     setLock: PropTypes.func,
     setShowShortId: PropTypes.func,
     field: PropTypes.string,
     className: PropTypes.string,
     style: PropTypes.any,
};

export default React.memo(ReportTableCompare);
